<script>
  import { mapState } from 'vuex'
  import { validationMixin } from 'vuelidate'
  import { required, maxLength } from 'vuelidate/lib/validators'
  import { exactLength } from '@/validators'
  import ChangePasswordForm from '@/components/ChangePasswordForm'
  import Uploader from '@/components/Uploader'
  import PlayerApi from '@/services/Api/PlayerApi'

  export default {
    mixins: [validationMixin],

    components: {
      ChangePasswordForm,
      Uploader
    },

    data() {
      return {
        tab: 0,
        loading: false,
        loading_img: false,
        loading_error: false,
        profile: {}
      }
    },

    created() {
      this.$store.dispatch('getLeagues', {
        school_id: this.user.player.school_id
      })

      this.profile = {
        firstname: this.user.player.firstname,
        lastname: this.user.player.lastname,
        league: this.user.player.league_id,
        promo: this.user.player.graduating_year,
        description: this.user.player.description,
        job: this.user.player.original_job
      }
    },

    computed: {
      ...mapState(['leagues'])
    },

    validations() {
      return {
        profile: {
          firstname: {
            required,
            maxLength: maxLength(150)
          },
          lastname: {
            required,
            maxLength: maxLength(150)
          },
          league: {
            required
          },
          job: {
            maxLength: maxLength(100)
          },
          description: {
            maxLength: maxLength(500)
          },
          promo: {
            required,
            exactLength: exactLength(4)
          }
        }
      }
    },

    methods: {
      uploadStart() {
        this.loading_img = true
        this.loading_error = null
      },
      uploadError(error) {
        this.loading_img = false

        if (error.response.status === 422) {
          this.loading_error = error.response.data.file[0]
        } else {
          this.loading_error = 'errors.' + error.response.data.error
        }
      },
      uploadDone(response) {
        this.loading_img = false
        this.loading_error = null
        this.profile.picture_path = response.data.path
      },
      saveInfos() {
        this.$v.$touch()

        if (this.$v.$error) {
          return
        }

        this.loading = true
        PlayerApi.update(this.user.player.id, {
          firstname: this.profile.firstname,
          lastname: this.profile.lastname,
          description: this.profile.description,
          job: this.profile.job,
          league_id: this.profile.league,
          picture_path: this.profile.picture_path,
          graduating_year: this.profile.promo
        })
        .then(player => {
          Object.assign(this.user.player, player)

          this.$store.dispatch('Alerts/add', {
            trans: 'messages.profile_updated',
            type: 'success'
          })
        })
        .finally(() => {
          this.loading = false
          this.$store.commit('Modals/hide', 'edit_profile')
        })
      }
    }
  }
</script>

<template>
  <div class="Edit-profile-form modal-card">
    <header class="modal-card-head">
      <h2 class="modal-card-title">
        {{ $t('profile_edit.title') }}
      </h2>
      <button class="delete" type="button" @click="$parent.$emit('close')"></button>
    </header>

    <div class="modal-card-body">
      <p class="app-simple-loader" v-show="!leagues.length"></p>

      <div class="tabs is-fullwidth">
        <ul>
          <li :class="{'is-active': tab == 0}">
            <a @click="tab = 0">{{ $t('profile_edit.tab_info') }}</a>
          </li>
          <li :class="{'is-active': tab == 1}">
            <a @click="tab = 1">{{ $t('profile_edit.tab_password') }}</a>
          </li>
        </ul>
      </div>
      
      <div class="Edit-profile-form_contents" v-show="leagues.length">
        <form v-if="tab === 0" @submit.prevent="saveInfos">
          <div class="Edit-profile-form_image">
            <uploader class="button is-primary is-outlined is-flat" url="upload" @start="uploadStart" @done="uploadDone" @error="uploadError">
              <span class="icon">
                <i class="fa fa-file-image-o"></i>
              </span>
              <span v-html="$t('buttons.click_to_upload')"></span>
            </uploader>
            <img v-if="profile.picture_path" :src="profile.picture_path">
            <p class="help is-info" v-show="loading_img">{{ $t('loading') }}</p>
            <p class="help is-danger" v-show="loading_error">
              {{ $t(loading_error, {max: loading_error == 'validation.max.file' ? '2Mo': ''}) }}
            </p>
          </div>
          <div class="columns">
            <div class="column">
              <p class="control has-icon">
                <input class="input" :class="{'is-danger': $v.profile.firstname.$error}" type="text" v-model="profile.firstname" :placeholder="$t('register.field.firstname')">
                <span class="icon">
                  <i class="fa fa-user-o"></i>
                </span>
              </p>
              <p class="help is-danger" v-show="$v.profile.firstname.$error &&!$v.profile.firstname.required">
                {{ $t('validation.required') }}
              </p>
              <p class="help is-danger" v-show="$v.profile.firstname.$error &&!$v.profile.firstname.maxLength">
                {{ $t('validation.max.string', {max: 190}) }}
              </p>
            </div>
            <div class="column">
              <p class="control has-icon">
                <input class="input" :class="{'is-danger': $v.profile.lastname.$error}" type="text" v-model="profile.lastname" :placeholder="$t('register.field.lastname')">
                <span class="icon">
                  <i class="fa fa-user-o"></i>
                </span>
              </p>
              <p class="help is-danger" v-show="$v.profile.lastname.$error &&!$v.profile.lastname.required">
                {{ $t('validation.required') }}
              </p>
              <p class="help is-danger" v-show="$v.profile.lastname.$error &&!$v.profile.lastname.maxLength">
                {{ $t('validation.max.string', {max: 190}) }}
              </p>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <p class="control">
                <span class="select" :class="{'is-danger': $v.profile.league.$error}">
                  <select v-model="profile.league">
                    <option value="">{{ $t('register.field.league') }}</option>
                    <option v-for="league in leagues" :value="league.id">
                      {{ league.name }}
                    </option>
                  </select>
                </span>
              </p>
              <p class="help is-danger" v-show="$v.profile.league.$error &&!$v.profile.league.required">
                {{ $t('validation.required') }}
              </p>
            </div>
            <div class="column">
              <p class="control has-icon">
                <input class="input" :class="{'is-danger': $v.profile.promo.$error}" type="number" v-model.number="profile.promo" :placeholder="$t('register.field.promo')" min="2010">
                <span class="icon">
                  <i class="fa fa-graduation-cap"></i>
                </span>
              </p>
              <p class="help is-danger" v-show="$v.profile.promo.$error &&!$v.profile.promo.required">
                {{ $t('validation.required') }}
              </p>
              <p class="help is-danger" v-show="$v.profile.promo.$error &&!$v.profile.promo.exactLength">
                {{ $t('validation.size.string', {size: 4}) }}
              </p>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <p class="control has-icon">
                <input class="input" :class="{'is-danger': $v.profile.job.$error}" type="text" v-model="profile.job" :placeholder="$t('register.field.job')">
                <span class="icon">
                  <i class="fa fa-suitcase"></i>
                </span>
              </p>
              <p class="help is-danger" v-show="$v.profile.job.$error &&!$v.profile.job.maxLength">
                {{ $t('validation.max.string', {max: 100}) }}
              </p>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <p class="control">
                <textarea class="textarea" :class="{'is-danger': $v.profile.description.$error}"v-model="profile.description" :placeholder="$t('register.field.description')"></textarea>
              </p>
              <p class="help is-danger" v-show="$v.profile.description.$error &&!$v.profile.description.maxLength">
                {{ $t('validation.max.string', {max: 500}) }}
              </p>
            </div>
          </div>
          <div class="register-form_actions">
            <button class="button is-danger is-fullwidth" :class="{'is-loading': loading}" type="submit">{{ $t('buttons.update') }}</button>
          </div>
        </form>

        <change-password-form v-if="tab === 1"></change-password-form>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .Edit-profile-form {
    .modal-card-head {
      text-align: center;
    }

    .tabs:not(:last-child) {
      margin-bottom: 2rem;
    }

    &_image {
      &,
      .button {
        display: block;
        width: 100%;
        margin-bottom: 2rem;
      }

      .button {
        line-height: 24px;

        i {
          margin-right: 1.5rem;
        }
      }

      img {
        display: block;
        max-width: 200px;
        margin: 0 auto;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// ModalEditProfile.vue?42c72d78